import { useEffect, useRef, useState, type RefObject } from "react";
import { ResilientVideoController, type VideoDiagnostic } from "./resilientVideoController";

const initialDiagnostic: VideoDiagnostic = {
  timestamp: "", eventName: "initialised", currentTime: 0, duration: null,
  paused: true, ended: false, readyState: 0, networkState: 0, errorCode: null,
  visibilityState: "visible", recoveryAttempt: 0, watchdogStatus: "idle"
};

type Options = {
  videoRef: RefObject<HTMLVideoElement | null>;
  active: boolean;
  source: string | null;
  debug?: boolean;
};

export function useResilientVideoPlayback({ videoRef, active, source, debug = false }: Options) {
  const activeRef = useRef(active);
  const sourceRef = useRef(source);
  const controllerRef = useRef<ResilientVideoController | null>(null);
  const [diagnostic, setDiagnostic] = useState(initialDiagnostic);
  activeRef.current = active;
  sourceRef.current = source;

  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    const controller = new ResilientVideoController({
      video: element,
      isActive: () => activeRef.current,
      getSource: () => sourceRef.current,
      debug,
      onDiagnostic: debug ? setDiagnostic : undefined
    });
    controllerRef.current = controller;
    controller.start();
    return () => {
      controller.stop();
      controllerRef.current = null;
    };
  }, [debug, videoRef]);

  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    if (source) {
      if (element.getAttribute("src") !== source) {
        element.src = source;
        element.load();
      }
    } else {
      element.pause();
      element.removeAttribute("src");
      element.load();
    }
    controllerRef.current?.sourceChanged();
  }, [source, videoRef]);

  useEffect(() => {
    controllerRef.current?.setActive(active);
  }, [active]);

  return diagnostic;
}